import React, { useState, ReactNode } from 'react';
import { IconButton } from '@mui/material';
import { Add } from '@mui/icons-material';

import { OccupyFreeSpace } from './ActionRow';
import { Form, FormRow } from './Forms';
import { LoadingIndicator } from './LoadingIndicator';

import { ButtonOptionPicker } from './ButtonOptionPicker';
import { FieldDefinition, FieldSettingsPopupButton, FieldsSettings, TableForFields, TableProps } from '../schemed';
import { SearchField } from './SearchField';
import { Schema } from '../../hooks/useSchema';
import { CrudListData } from '../../api/useSimpleCrud';
import { PagingControl } from './PagingControl';
import { PagedLoadedData } from '../../hooks/useLoadedData';
import { useWindowHotkey } from './hotkeys';
import { FieldSorting } from '../../hooks/useFieldSorting';
import { FieldFilters, FieldFiltersPopupButton } from '../schemed/Filtering';

interface Props<T> {
    data: CrudListData<T>;
    schema: Schema;
    title: ReactNode;
    headerActions?: ReactNode;
    headerViewActions?: ReactNode;
    tabs?: [string, string][];
    fields: FieldDefinition[];
    linkFields: string[];
    getLinkValue: (item: T) => string;
    totalRecordsText?: string;
    rowButtons?: (row: T) => ReactNode;
    onRowClick?: (row: T) => void;
    searchAutoFocus?: boolean;
    searchAuto?: boolean;
    noNewItemHotkey?: boolean;
    fieldElement?: TableProps<T>["fieldElement"];
    fieldSetings?: FieldsSettings;
    fieldFilters?: FieldFilters;
    sorting?: FieldSorting;
    noCreate?: boolean;
    tableProps?: Partial<TableProps<T>>; 
}

export const SimpleList = <T extends unknown>(props: Props<T>) => {
    const { data, schema, title, tabs, fields, linkFields, getLinkValue, totalRecordsText, rowButtons, onRowClick, searchAutoFocus } = props;

    const [textFilter, setTextFilter] = useState<string>("");

    useWindowHotkey("alt+n", () => props.noNewItemHotkey ? null : data.create());
    
    return (
        <Form title={title}
            headerItems={<>
                {!props.noCreate && <IconButton color='primary' size="small" onClick={() => data.create()}><Add /></IconButton>}
                {props.headerActions}
                <OccupyFreeSpace />
                {data.isLoading && <LoadingIndicator sizeVariant="m" />}
                <SearchField
                    filter={props.searchAuto ? data.filter : textFilter}
                    setFilter={props.searchAuto ? data.setFilter : setTextFilter}
                    doSearch={props.searchAuto ? undefined : () => data.setFilter(textFilter)}
                    buttonLabel=" "
                    noButton={props.searchAuto}
                    autoFocus={searchAutoFocus}
                />
                {props.headerViewActions}
                {tabs && tabs.length > 0 && <ButtonOptionPicker
                    options={tabs}
                    selected={data.view}
                    setSelected={data.setView} />}
                {props.fieldFilters && <FieldFiltersPopupButton fieldFilters={props.fieldFilters} />}
                {props.fieldSetings && <FieldSettingsPopupButton fieldsSettings={props.fieldSetings} />}
                    </>}>

            {!!(data as PagedLoadedData<T>).paging && <FormRow>
                <PagingControl data={(data as PagedLoadedData<T>)} totalRecordsText={totalRecordsText} />
            </FormRow>}

            <TableForFields
                {...(props.tableProps || {})}
                fields={props.fieldSetings ? props.fieldSetings?.activeFields : fields}
                fieldLink={field => linkFields.find(f => f === field) ?  getLinkValue : null}
                data={data.data}
                schema={schema}
                rowButtons={rowButtons}
                onRowClick={onRowClick}
                fieldElement={props.fieldElement}
                sorting={props.sorting}

                />
            
        </Form>
    );
}